import { useState } from 'react';
import { motion } from 'framer-motion';
import { PartyPopper, Presentation, Users, Home, Coffee, Sandwich, MapPin, Armchair, Phone, Send } from 'lucide-react';
import { HamburgerMenu } from '../components/HamburgerMenu';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { SEO } from '../components/SEO';

/**
 * ─────────────────────────────────────────────────────────────
 * PASTE YOUR FORMSPREE ENDPOINT HERE
 *
 * 1. Create a form at https://formspree.io
 * 2. Copy the endpoint it gives you (looks like https://formspree.io/f/abcdwxyz)
 * 3. Replace the placeholder string below with it.
 *
 * Until this is replaced, the form shows a notice instead of submitting.
 * ─────────────────────────────────────────────────────────────
 */
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/REPLACE_WITH_YOUR_FORM_ID';
const isEndpointConfigured = !FORMSPREE_ENDPOINT.includes('REPLACE_WITH');
const greatFor = [{
  icon: PartyPopper,
  name: 'Private Parties & Celebrations',
  description: 'Birthdays, showers, retirements, and the milestones worth marking together.'
}, {
  icon: Presentation,
  name: 'Business Meetings & Workshops',
  description: 'Offsites, trainings, and client sessions in a room that feels nothing like a conference center.'
}, {
  icon: Users,
  name: 'Community Groups & Clubs',
  description: 'Book clubs, nonprofit boards, meetups, and standing weekly gatherings.'
}, {
  icon: Home,
  name: 'Family Gatherings',
  description: 'Room for groups and families, with a menu that works for every age at the table.'
}];
const included = [{
  icon: Armchair,
  name: 'Our Welcoming Cafe Atmosphere',
  description: 'The same warm room our regulars settle into every morning, set aside for your group.'
}, {
  icon: Coffee,
  name: 'Coffee & Espresso Service',
  description: 'Our full bar on site — drip, espresso drinks, matcha, chai, and cold brew.'
}, {
  icon: Sandwich,
  name: 'Optional Food Catering',
  description: 'Add pastries, breakfast sandwiches, toasts, or salads from our kitchen.'
}, {
  icon: MapPin,
  name: 'Central Livermore Location',
  description: 'Easy to find on Wright Brothers Ave, with free parking right outside.'
}];
const eventTypes = ['Private party or celebration', 'Business meeting or workshop', 'Community group or club', 'Family gathering', 'Pop-up or market', 'Something else'];
export function Events() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isEndpointConfigured) {
      setStatus('error');
      return;
    }
    const form = e.currentTarget;
    setStatus('loading');
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: new FormData(form)
      });
      if (!response.ok) throw new Error('Request failed');
      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
    }
  };
  const fieldClasses = 'w-full bg-white/5 border border-white/10 text-white px-4 py-3 font-inter text-base focus:outline-none focus:border-[#D97E3A] focus:bg-white/10 transition-all placeholder:text-white/30';
  const labelClasses = 'block font-inter text-sm font-medium text-white/80 mb-2';
  return <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#D97E3A] selection:text-white" data-id="element-333" data-mp="2yno48lt9rc3e">
      <SEO title="Rent Our Community Space in Livermore | Private Events at Grounded" description="Host your next gathering at Grounded - a welcoming community space in Livermore available for private events, meetings, and celebrations, with in-house coffee and catering. Great for groups and families." canonical="https://www.groundedlivermore.com/events" data-id="element-334" data-mp="2tokyt09dn3uw" />
      <HamburgerMenu data-id="element-335" data-mp="uxi2nyr0aqfe" />

      <main data-id="element-336" data-mp="zvt87xa9qert">
        {/* Header */}
        <header className="pt-32 pb-20 px-6 md:px-12 lg:px-24 max-w-5xl mx-auto" data-id="element-337" data-mp="zurx5st95sa3">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} data-id="element-338" data-mp="kz28fmwon11o">
            <div className="flex items-center gap-3 mb-6" data-id="element-339" data-mp="c68mx7qn75fd">
              <div className="h-px w-12 bg-[#D97E3A]" data-id="element-340" data-mp="c4xm9gshudsh" />
              <span className="text-[#D97E3A] font-inter font-medium tracking-widest text-xs uppercase" data-id="element-341" data-mp="gxh1ei6txgze">
                Private Events · Livermore, CA
              </span>
            </div>

            <h1 className="font-bebas text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-8" data-id="element-342" data-mp="76w18qbic4ed">
              Rent Our Community Space in Livermore
            </h1>

            <p className="font-inter text-lg text-white/70 leading-relaxed max-w-3xl border-l-2 border-[#D97E3A] pl-6" data-id="element-343" data-mp="3pevaih3x01bo">
              Grounded is more than a coffee shop — it's a gathering place. Our
              community space is available to rent for private events, meetings,
              celebrations, and pop-ups, with our full coffee bar and catering
              available on site.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4" data-id="element-344" data-mp="3jodnz7mtw8k9">
              <a href="#inquire" className="inline-flex items-center justify-center gap-3 bg-[#D97E3A] text-white px-8 py-4 font-bebas text-xl tracking-wide hover:bg-[#c06b2e] transition-colors" data-id="element-345" data-mp="3eq3s0uta53xl">
                <Send size={20} aria-hidden="true" data-id="element-346" data-mp="6bu0cuyo7btx" />
                CHECK AVAILABILITY
              </a>
              <a href="tel:+19252920433" className="inline-flex items-center justify-center gap-3 border border-white text-white px-8 py-4 font-bebas text-xl tracking-wide hover:border-[#D97E3A] hover:text-[#D97E3A] transition-colors" data-id="element-347" data-mp="2aibwsrj8wnb">
                <Phone size={20} aria-hidden="true" data-id="element-348" data-mp="b93rqg5mdxke" />
                CALL TO BOOK: (925) 292-0433
              </a>
            </div>
          </motion.div>
        </header>

        {/* Great For */}
        <section aria-labelledby="great-for-heading" className="px-6 md:px-12 lg:px-24 py-20 bg-[#111] border-y border-white/10" data-id="element-349" data-mp="3925i3jp3sy62">
          <div className="max-w-5xl mx-auto" data-id="element-350" data-mp="3iv2zfmid0h33">
            <motion.h2 id="great-for-heading" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="font-bebas text-5xl md:text-6xl leading-none mb-12" data-id="element-351" data-mp="3ntebm3w4n3oh">
              Great <span className="text-[#D97E3A]" data-id="element-352" data-mp="5mq8ukojuslz">For</span>
            </motion.h2>

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-353" data-mp="22gl2vdsxwb7e">
              {greatFor.map((item, i) => <motion.div key={item.name} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className="border border-white/10 p-8 bg-[#0f0f0f]" data-id="element-354" data-mp="27g7drglapfd1" data-mpi={i}>
                  <item.icon className="text-[#D97E3A] mb-4" size={30} strokeWidth={1.5} aria-hidden="true" data-id="element-355" data-mp="1l6qx83s8fk2l" />
                  <dt className="font-bebas text-2xl tracking-wide mb-2" data-id="element-356" data-mp="2dvo2n6bchmdx">
                    {item.name}
                  </dt>
                  <dd className="font-inter text-base text-white/60 leading-relaxed" data-id="element-357" data-mp="28va54i7dduu5">
                    {item.description}
                  </dd>
                </motion.div>)}
            </dl>
          </div>
        </section>

        {/* Included */}
        <section aria-labelledby="included-heading" className="px-6 md:px-12 lg:px-24 py-20" data-id="element-358" data-mp="tft3qxi1jvg2">
          <div className="max-w-5xl mx-auto" data-id="element-359" data-mp="2sk1igbgjhm79">
            <motion.h2 id="included-heading" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="font-bebas text-5xl md:text-6xl leading-none mb-4" data-id="element-360" data-mp="2smffgpus58hg">
              What's <span className="text-[#D97E3A]" data-id="element-361" data-mp="3c7s8x9cnilrg">Included</span>
            </motion.h2>
            <p className="font-inter text-base text-white/50 mb-12 max-w-2xl" data-id="element-362" data-mp="3g9njl4gh2bhw">
              Capacity and pricing depend on the day and the shape of your
              event — contact us for details.
            </p>

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10" data-id="element-363" data-mp="37h25w2rp6uzx">
              {included.map((item, i) => <motion.div key={item.name} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className="flex gap-4" data-id="element-364" data-mp="3v6easqfzsgwf" data-mpi={i}>
                  <item.icon className="text-[#D97E3A] shrink-0 mt-1" size={26} strokeWidth={1.5} aria-hidden="true" data-id="element-365" data-mp="93w4fimwzaml" />
                  <div data-id="element-366" data-mp="3qdu3ltm86r83">
                    <dt className="font-bebas text-2xl tracking-wide mb-2" data-id="element-367" data-mp="3qd2bisru7bmk">
                      {item.name}
                    </dt>
                    <dd className="font-inter text-base text-white/60 leading-relaxed" data-id="element-368" data-mp="3me1unfsx6j27">
                      {item.description}
                    </dd>
                  </div>
                </motion.div>)}
            </dl>
          </div>
        </section>

        {/* Inquiry Form */}
        <section id="inquire" aria-labelledby="inquire-heading" className="px-6 md:px-12 lg:px-24 py-20 bg-[#111] border-y border-white/10 scroll-mt-8" data-id="element-369" data-mp="1fjy23272f2y8">
          <div className="max-w-3xl mx-auto" data-id="element-370" data-mp="1dlkjuhvp6edz">
            <h2 id="inquire-heading" className="font-bebas text-5xl md:text-6xl leading-none mb-4" data-id="element-371" data-mp="18nan42ubr5q9">
              Tell Us About Your <span className="text-[#D97E3A]" data-id="element-372" data-mp="13uhj5h6ok1f8">Event</span>
            </h2>
            <p className="font-inter text-base text-white/60 mb-10 leading-relaxed" data-id="element-373" data-mp="ywfzau1fjy2p">
              Send us the details and we'll get back to you with availability
              and pricing. Prefer to talk it through?{' '}
              <a href="tel:+19252920433" className="text-[#D97E3A] hover:underline" data-id="element-374" data-mp="pv82yhih57b9">
                Call to book: (925) 292-0433
              </a>
              .
            </p>

            {status === 'success' ? <motion.div initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} role="status" className="border border-[#D97E3A] bg-[#D97E3A]/10 p-8 text-center" data-id="element-375" data-mp="2faehtx4blzpn">
                <h3 className="font-bebas text-3xl tracking-wide text-[#D97E3A] mb-3" data-id="element-376" data-mp="2muyttmujd048">
                  Request Received
                </h3>
                <p className="font-inter text-white/80" data-id="element-377" data-mp="2rmqfpk2qyho1">
                  Thanks for reaching out. We'll be in touch shortly with
                  availability and details.
                </p>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-378" data-mp="1zwlszerilijn">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-379" data-mp="23ww3n4q94x4u">
                  <div data-id="element-380" data-mp="28uxuibtx60qv">
                    <label htmlFor="name" className={labelClasses} data-id="element-381" data-mp="28ufmyj1t403n">
                      Name
                    </label>
                    <input id="name" name="name" type="text" required autoComplete="name" className={fieldClasses} placeholder="Your name" data-id="element-382" data-mp="2ds7g1ge6ts9f" />
                  </div>
                  <div data-id="element-383" data-mp="2umqil2o6we6x">
                    <label htmlFor="email" className={labelClasses} data-id="element-384" data-mp="2ul73vgi6mb79">
                      Email
                    </label>
                    <input id="email" name="email" type="email" required autoComplete="email" className={fieldClasses} placeholder="you@example.com" data-id="element-385" data-mp="39ipzn2ta1oo7" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-386" data-mp="2qlorj94ff8g1">
                  <div data-id="element-387" data-mp="2lu6omfiuexsf">
                    <label htmlFor="phone" className={labelClasses} data-id="element-388" data-mp="2lsvmsl8yk03y">
                      Phone
                    </label>
                    <input id="phone" name="phone" type="tel" autoComplete="tel" className={fieldClasses} placeholder="(925) 555-0123" data-id="element-389" data-mp="1y2htkremzv7s" />
                  </div>
                  <div data-id="element-390" data-mp="dg08b9jxs6q0">
                    <label htmlFor="eventType" className={labelClasses} data-id="element-391" data-mp="3uuf2b54vu70z">
                      Event Type
                    </label>
                    <select id="eventType" name="eventType" required defaultValue="" className={fieldClasses} data-id="element-392" data-mp="3p5kw1uyf12z">
                      <option value="" disabled data-id="element-393" data-mp="waoy2oz4aakl">
                        Select one
                      </option>
                      {eventTypes.map(type => <option key={type} value={type} className="bg-[#111]" data-id="element-394" data-mp="mdaierl0pptc">
                          {type}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-395" data-mp="372hwpvs10b8b">
                  <div data-id="element-396" data-mp="3nda63scvpzv7">
                    <label htmlFor="preferredDate" className={labelClasses} data-id="element-397" data-mp="3nds8z7u1rcc3">
                      Preferred Date
                    </label>
                    <input id="preferredDate" name="preferredDate" type="date" className={fieldClasses} data-id="element-398" data-mp="3dn7h038vp625" />
                  </div>
                  <div data-id="element-399" data-mp="a3ctxqeqg2sd">
                    <label htmlFor="estimatedGuests" className={labelClasses} data-id="element-400" data-mp="56cl57qi0lxk">
                      Estimated Guests
                    </label>
                    <input id="estimatedGuests" name="estimatedGuests" type="number" min="1" className={fieldClasses} placeholder="25" data-id="element-401" data-mp="5yd3powdbzj" />
                  </div>
                </div>

                <div data-id="element-402" data-mp="2gxzq0fphq4qu">
                  <label htmlFor="message" className={labelClasses} data-id="element-403" data-mp="we4f1592f9uz">
                    Message
                  </label>
                  <textarea id="message" name="message" rows={5} className={`${fieldClasses} resize-y`} placeholder="Tell us what you have in mind — timing, food, setup, anything we should know." data-id="element-404" data-mp="10eoeifh42pka" />
                </div>

                {status === 'error' && <p role="alert" className="font-inter text-sm text-red-400" data-id="element-405" data-mp="1k36xugffr8r2">
                    {isEndpointConfigured ? "Something went wrong sending your request. Please try again, or call us at (925) 292-0433." : 'This form is not connected yet. Please call (925) 292-0433 to book in the meantime.'}
                  </p>}

                <button type="submit" disabled={status === 'loading'} className="inline-flex items-center justify-center gap-3 bg-[#D97E3A] text-white px-8 py-4 font-bebas text-xl tracking-wide hover:bg-[#c06b2e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto" data-id="element-406" data-mp="3uvi1b892ovyb">
                  {status === 'loading' ? <>
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" data-id="element-407" data-mp="161tpvcwhfc4r" />
                      SENDING
                    </> : <>
                      SEND REQUEST
                      <Send size={18} aria-hidden="true" data-id="element-408" data-mp="1tu0hjyr9iuxu" />
                    </>}
                </button>
              </form>}
          </div>
        </section>

        <NewsletterSignup data-id="element-409" data-mp="23o97qfstmbsu" />

        <footer className="border-t border-white/10 py-12 text-center" data-id="element-410" data-mp="1yrir9rd3te6w">
          <p className="font-bebas text-2xl text-white/50" data-id="element-411" data-mp="1yp63gxko09lx">
            © {new Date().getFullYear()} GROUNDED LIVERMORE
          </p>
        </footer>
      </main>
    </div>;
}